// How a scale cell is written on the staff: the key signature it sits under and
// the tonic name it is written from.
//
// Key signatures by family:
// - Major (Ionian): its own key signature.
// - The diatonic modes: the signature of the major key the mode belongs to
//   (B Dorian, F# Aeolian and G# Locrian all take A major's).
// - Harmonic minor and melodic minor: the tonic's minor key signature (the
//   relative major's: F# harmonic minor takes A major's), the raised notes
//   written as accidentals.
// - The symmetric scales (whole-tone and the two diminished scales): no key
//   signature, accidentals only. No signature fits them better than another.
// - Every other scale: the signature that leaves the fewest accidentals in the
//   run. A tie goes to the tonic's own major or minor signature (the one whose
//   third the scale has, then the other), then to a signature that leaves the
//   tonic itself unmarked, then to the signature with fewer sharps or flats. This is the parent-key rule the modes follow, extended to
//   the derived scales: F# Lydian dominant sits under B major's five sharps with
//   only B# marked (a Mixolydian with a raised 4th), A# Superlocrian under B
//   major's with only the D natural marked.
// A signature never changes a note's letter (scaleSpelling.ts spells every
// note); it only changes which notes need an accidental drawn. The "accidentals
// only" display drops the signature for every scale.
//
// Theoretical keys: a cell's tonic label is fixed (Db, never C#), so a few cells
// would be written in a key past seven sharps or flats (Db minor: Fb, Bbb, Cb),
// or with double accidentals that the tonic's other name avoids. Those cells are
// written from the enharmonic tonic (C# minor). The cell's identity (scale and
// tonic label) and every pitch it sounds are unchanged; only the writing is.

import type { Tonic } from 'Types/ExerciseContentTypes';
import { scaleFormula } from './scaleFormulas';
import { spellScale } from './scaleSpelling';
import type { EnharmonicTonic, NoteLetter, SpelledNote, TonicName } from './scaleSpelling';

/** The display setting: the scale's key signature, or none (accidentals only). */
export type KeySignatureDisplay = 'show' | 'accidentals_only';

export const KEY_SIGNATURE_DISPLAYS: readonly KeySignatureDisplay[] = ['show', 'accidentals_only'];

export const DEFAULT_KEY_SIGNATURE_DISPLAY: KeySignatureDisplay = 'show';

/** Which key signature a scale family is written under. */
export type KeySignatureRule =
  | 'own_major'
  | 'parent_major'
  | 'tonic_minor'
  | 'fewest_accidentals'
  | 'none';

const DIATONIC_MODE_UNIT_IDS: readonly string[] = [
  'dorian',
  'phrygian',
  'lydian',
  'mixolydian',
  'aeolian',
  'locrian',
];

const MINOR_KEY_UNIT_IDS: readonly string[] = ['harmonic-minor', 'ascending-melodic-minor'];

/** The symmetric scales: written with accidentals only, whatever the tonic. */
const SYMMETRIC_UNIT_IDS: readonly string[] = [
  'whole-tone',
  'fully-diminished',
  'dominant-diminished',
];

const ENHARMONIC_TONICS: Partial<Record<Tonic, EnharmonicTonic>> = {
  'F#': 'Gb',
  Db: 'C#',
  Ab: 'G#',
  Eb: 'D#',
  Bb: 'A#',
};

/** The most sharps or flats a real key signature carries. */
const MAX_KEY_SIGNATURE_ACCIDENTALS = 7;

export const keySignatureRule = (unitId: string): KeySignatureRule => {
  if (unitId === 'ionian') return 'own_major';
  if (DIATONIC_MODE_UNIT_IDS.includes(unitId)) return 'parent_major';
  if (MINOR_KEY_UNIT_IDS.includes(unitId)) return 'tonic_minor';
  if (SYMMETRIC_UNIT_IDS.includes(unitId)) return 'none';
  return 'fewest_accidentals';
};

/** The letters a key signature alters, in the order its sharps (or, reversed, its flats) appear. */
const SHARP_ORDER: readonly NoteLetter[] = ['F', 'C', 'G', 'D', 'A', 'E', 'B'];

/** The alteration a key signature (in fifths) puts on a letter: +1, -1 or 0. */
export const signatureAlteration = (fifths: number, letter: NoteLetter): number => {
  if (fifths > 0) return SHARP_ORDER.indexOf(letter) < fifths ? 1 : 0;
  if (fifths < 0) return [...SHARP_ORDER].reverse().indexOf(letter) < -fifths ? -1 : 0;
  return 0;
};

/**
 * How many of a scale's notes (one octave, as spelled) need an accidental drawn under a key
 * signature: every note whose alteration is not the one the signature gives its letter.
 */
export const inlineAccidentalCount = (notes: readonly SpelledNote[], fifths: number): number =>
  notes.filter((note) => note.alteration !== signatureAlteration(fifths, note.letter)).length;

/** Every real key signature, in fifths: seven flats to seven sharps. */
const KEY_SIGNATURES: readonly number[] = Array.from(
  { length: 2 * MAX_KEY_SIGNATURE_ACCIDENTALS + 1 },
  (_unused, index) => index - MAX_KEY_SIGNATURE_ACCIDENTALS,
);

/** True when the scale has a major third above its tonic (its own key is then the major one). */
const hasMajorThird = (unitId: string): boolean => scaleFormula(unitId).includes('3');

/**
 * The signature leaving the fewest accidentals in the scale; ties go to the tonic's own major or
 * minor signature (the one whose third the scale has first), then to a signature that leaves the
 * tonic itself unmarked (A# Superlocrian under B major's, its parent Locrian's, not A major's),
 * then to fewer sharps or flats, then to the side (sharps or flats) the scale's spelling leans to.
 */
const fewestAccidentalsFifths = (unitId: string, tonic: TonicName): number => {
  const notes = spellScale(unitId, tonic);
  const ownMajor = alterationTotal(spellScale('ionian', tonic));
  const ownMinor = alterationTotal(spellScale('aeolian', tonic));
  const [preferredOwn, otherOwn] = hasMajorThird(unitId)
    ? [ownMajor, ownMinor]
    : [ownMinor, ownMajor];
  const ownRank = (fifths: number): number =>
    fifths === preferredOwn ? 0 : fifths === otherOwn ? 1 : 2;
  const lean = Math.sign(alterationTotal(notes));
  const tonicMarked = (fifths: number): number =>
    Number(notes[0].alteration !== signatureAlteration(fifths, notes[0].letter));
  const [best] = [...KEY_SIGNATURES].sort(
    (a, b) =>
      inlineAccidentalCount(notes, a) - inlineAccidentalCount(notes, b) ||
      ownRank(a) - ownRank(b) ||
      tonicMarked(a) - tonicMarked(b) ||
      Math.abs(a) - Math.abs(b) ||
      Number(Math.sign(b) === lean) - Number(Math.sign(a) === lean),
  );
  return best;
};

const alterationTotal = (notes: SpelledNote[]): number =>
  notes.reduce((total, note) => total + note.alteration, 0);

/**
 * The key signature, in fifths (sharps positive, flats negative), of the scale
 * written from `tonic`; 0 for a scale written without one. A diatonic scale's
 * seven letters each carry its key's accidental, so their alterations add up to
 * the key's fifths (a value past seven is a theoretical key).
 */
export const keySignatureFifths = (unitId: string, tonic: TonicName): number => {
  switch (keySignatureRule(unitId)) {
    case 'own_major':
    case 'parent_major':
      return alterationTotal(spellScale(unitId, tonic));
    case 'tonic_minor':
      return alterationTotal(spellScale('aeolian', tonic));
    case 'fewest_accidentals':
      return fewestAccidentalsFifths(unitId, tonic);
    default:
      return 0;
  }
};

const isTheoreticalKey = (fifths: number): boolean =>
  Math.abs(fifths) > MAX_KEY_SIGNATURE_ACCIDENTALS;

/** How many double accidentals, and how many accidentals in all, the scale's notes carry. */
const accidentalCounts = (unitId: string, tonic: TonicName): { doubles: number; all: number } =>
  spellScale(unitId, tonic).reduce(
    (counts, note) => ({
      doubles: counts.doubles + (Math.abs(note.alteration) >= 2 ? 1 : 0),
      all: counts.all + Math.abs(note.alteration),
    }),
    { doubles: 0, all: 0 },
  );

/**
 * The tonic name a cell is written from: its own label, or the label's
 * enharmonic name when the label would write a theoretical key, or double
 * accidentals the enharmonic name writes fewer of (with fewer accidentals in all).
 */
export const writtenTonic = (unitId: string, tonic: Tonic): TonicName => {
  const enharmonic = ENHARMONIC_TONICS[tonic];
  if (!enharmonic) return tonic;
  const labelIsTheoretical = isTheoreticalKey(keySignatureFifths(unitId, tonic));
  if (labelIsTheoretical !== isTheoreticalKey(keySignatureFifths(unitId, enharmonic))) {
    return labelIsTheoretical ? enharmonic : tonic;
  }
  const label = accidentalCounts(unitId, tonic);
  const other = accidentalCounts(unitId, enharmonic);
  return other.doubles < label.doubles && other.all < label.all ? enharmonic : tonic;
};

/** How a cell is written: the tonic name its notes are spelled from, and its key signature. */
export interface ScaleNotation {
  tonic: TonicName;
  keySignatureFifths: number;
}

export const scaleNotation = (
  unitId: string,
  tonic: Tonic,
  display: KeySignatureDisplay,
): ScaleNotation => {
  const written = writtenTonic(unitId, tonic);
  return {
    tonic: written,
    keySignatureFifths: display === 'show' ? keySignatureFifths(unitId, written) : 0,
  };
};
